/**
 * Factory for creating staking protocol instances using the Factory Design Pattern.
 *
 * @remarks
 * This factory encapsulates the logic for instantiating different staking protocol
 * implementations based on protocol definitions. It provides validation, error handling,
 * and a clean separation of concerns for protocol creation.
 *
 * @remarks
 * Relation to protocol selection:
 * The StakingModule uses this factory to create concrete protocol instances and populate
 * the protocol maps (by id and by chain). These maps are later consumed by the
 * ProtocolSelectionStrategyManager and its strategies (e.g., ChainStrategy) to select
 * the appropriate protocol at request time based on the provided context (like chainId).
 * In other words, this factory is responsible for instantiation, while selection
 * strategies are responsible for choosing among the instantiated protocols.
 *
 * @example
 * ```typescript
 * const factory = new StakingProtocolFactory();
 *
 * const protocol = factory.create({
 *   name: "StakeWise",
 *   stakingProtocolType: "liquid-staking",
 *   logger: myLogger,
 *   chainService: myChainService,
 *   // ... other options
 * });
 * ```
 *
 * @internal
 * @packageDocumentation
 */
import { SolanaStakingProtocol } from "../protocols/solana/solana-staking-protocol";
import { StakewiseProtocol } from "../protocols/stakewise/stakewise-protocol";
import { StakingProtocolType } from "../staking-protocol";
/**
 * Factory class for creating staking protocol instances.
 *
 * @remarks
 * Implements the Factory Design Pattern to encapsulate protocol instantiation logic.
 * Provides validation, error handling, and support for multiple protocol types.
 *
 * **Supported Protocols:**
 * - StakeWise (liquid-staking)
 * - Future protocols can be easily added
 *
 * **Validation:**
 * - Ensures all required options are provided and non-empty
 * - Validates protocol type and name combinations
 * - Provides meaningful error messages for invalid configurations
 *
 * **Error Handling:**
 * - Catches and wraps instantiation errors
 * - Provides context about failed protocol creation
 * - Logs warnings for unsupported protocols
 */
export class StakingProtocolFactory {
    logger;
    static instance;
    constructor(logger) {
        this.logger = logger;
    }
    /**
     * Returns the singleton instance of the factory.
     */
    static getInstance(logger) {
        if (!StakingProtocolFactory.instance) {
            StakingProtocolFactory.instance = new StakingProtocolFactory(logger);
            return StakingProtocolFactory.instance;
        }
        return StakingProtocolFactory.instance;
    }
    /**
     * Creates a staking protocol instance based on the provided options.
     *
     * @param options - Configuration options for the protocol
     * @returns Instantiated staking protocol
     * @throws {Error} When validation fails or protocol instantiation fails
     *
     * @example
     * ```typescript
     * const protocol = factory.create({
     *   name: "StakeWise",
     *   description: "Liquid staking protocol",
     *   stakingProtocolType: "liquid-staking",
     *   availableChains: ["eip155:1", "eip155:560048"],
     *   logger: myLogger,
     *   chainService: myChainService
     * });
     * ```
     */
    create(options) {
        this.validateOptions(options);
        try {
            const protocolInstance = this.instantiateProtocol(options);
            this.logger.debug("Successfully created protocol instance", {
                name: options.name,
                type: options.stakingProtocolType,
                chains: options.availableChains,
            });
            return protocolInstance;
        }
        catch (error) {
            // this message is useful for debugging; make sure this is correctly propagated to the application
            const errorMessage = `Failed to create protocol instance for ${options.name}`;
            this.logger.debug(errorMessage, {
                name: options.name,
                type: options.stakingProtocolType,
            });
            // TBD define staking model errors (in error model)
            throw new Error(error instanceof Error ? error.message : errorMessage);
        }
    }
    /**
     * Validates the factory options to ensure all required fields are present and valid.
     *
     * @param options - Options to validate
     * @throws {Error} When validation fails
     * @private
     */
    validateOptions(options) {
        const errors = [];
        // Validate required string fields
        const requiredStringFields = [
            "name",
            "stakingProtocolType",
        ];
        for (const field of requiredStringFields) {
            const value = options[field];
            if (!value || typeof value !== "string" || value.trim() === "") {
                errors.push(`${field} is required and must be a non-empty string`);
            }
        }
        // Validate logger
        if (!options.logger) {
            errors.push("logger is required");
        }
        // Validate chainService
        if (!options.chainService) {
            errors.push("chainService is required");
        }
        // Validate availableChains
        if (!options.availableChains || !Array.isArray(options.availableChains)) {
            errors.push("availableChains is required and must be an array");
        }
        else if (options.availableChains.length === 0) {
            errors.push("availableChains must contain at least one chain");
        }
        else {
            // Validate each chain ID
            for (const chainId of options.availableChains) {
                if (!chainId || typeof chainId !== "string" || chainId.trim() === "") {
                    errors.push(`Invalid chain ID: ${chainId}. Chain IDs must be non-empty strings`);
                }
            }
        }
        // Validate optional URL if provided
        if (options.url && (typeof options.url !== "string" || options.url.trim() === "")) {
            errors.push("url must be a non-empty string if provided");
        }
        // Validate optional logoURI if provided
        if (options.logoURI && (typeof options.logoURI !== "string" || options.logoURI.trim() === "")) {
            errors.push("logoURI must be a non-empty string if provided");
        }
        if (errors.length > 0) {
            throw new Error(`Validation failed: ${errors.join(", ")}`);
        }
    }
    instantiateProtocol(options) {
        const { name, stakingProtocolType } = options;
        const protocolType = typeof stakingProtocolType === "string"
            ? stakingProtocolType
            : stakingProtocolType;
        if (name === "StakeWise" && protocolType === StakingProtocolType.LiquidStaking) {
            return new StakewiseProtocol({
                logger: options.logger,
                name: options.name,
                description: options.description,
                url: options.url,
                logoURI: options.logoURI,
                availableChains: options.availableChains,
                stakingProtocolType: protocolType,
                chainService: options.chainService,
                referralTag: options.referralTag,
                protocolConfig: options.protocolConfig,
            });
        }
        if (name === "Solana" && protocolType === StakingProtocolType.NativeStaking) {
            return new SolanaStakingProtocol({
                logger: options.logger,
                name: options.name,
                description: options.description,
                url: options.url,
                logoURI: options.logoURI,
                availableChains: options.availableChains,
                stakingProtocolType: protocolType,
                chainService: options.chainService,
                bdApiKey: options.bdApiKey,
                basePath: options.basePath,
                protocolConfig: options.protocolConfig,
            });
        }
        this.logger.debug(`No implementation found for protocol: ${name} (${protocolType})`);
        throw new Error(`No implementation found for protocol: ${name} with type: ${protocolType}`);
    }
}
//# sourceMappingURL=data:application/json;base64,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